import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Meta (Facebook) Pixel integration.
 *
 * - useFacebookPixel(): fires PageView on route changes — but ONLY on public
 *   marketing pages. Never fires on /dashboard (real patient/clinic data) or on
 *   the public patient flows (/confirm, /sign, /questionnaire, /book), so no
 *   patient data or device is ever sent to Meta.
 * - trackPixelEvent(): fires a standard event (Lead, Contact, CompleteRegistration,
 *   StartTrial…) with the same public-path guard. Never pass PII in params.
 *   Also pushes a mirror event to window.dataLayer (`ck_<lowercase name>`, e.g.
 *   `ck_lead`) so Google Tag Manager (GTM-MPJGWJXJ, loaded in index.html) can
 *   trigger GA4 / Google Ads conversion tags off the same user actions.
 *
 * The pixel loader + fbq('init', ...) live in public/index.html. This module is
 * the ONLY place fbq is called, which also avoids double-counting.
 */

// Exact paths that are marketing pages.
const PUBLIC_EXACT = new Set([
  '/',
  '/login',
  '/signup',
  '/start',
  '/terms-of-service',
  '/privacy-policy',
  '/accessibility',
]);

// Path prefixes that are marketing pages (SEO landing pages + blog).
const PUBLIC_PREFIXES = ['/for/', '/blog'];

export function isPublicMarketingPath(pathname) {
  if (PUBLIC_EXACT.has(pathname)) return true;
  return PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Fire a Meta Pixel standard event. Safe to call anywhere: no-ops when the pixel
 * is not loaded or when the current route is not a public marketing page.
 * @param {string} eventName - Meta standard event name (e.g. 'Lead', 'Contact')
 * @param {object} [params] - non-PII params (content_name, currency, value…)
 */
export function trackPixelEvent(eventName, params) {
  try {
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
    if (!isPublicMarketingPath(window.location.pathname)) return;
    if (params && Object.keys(params).length > 0) {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('track', eventName);
    }
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: `ck_${eventName.toLowerCase()}`, ...(params || {}) });
    }
  } catch (err) {
    // Analytics must never break the UI; swallow silently (no PII logged).
    console.error('[CK-1055] Meta Pixel track failed:', {
      errorCode: 'CK-1055',
      errorMessage: err?.message,
      eventName,
      timestamp: new Date().toISOString(),
    });
  }
}

export default function useFacebookPixel() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.fbq !== 'function') return;
    if (!isPublicMarketingPath(location.pathname)) return;
    window.fbq('track', 'PageView');
  }, [location.pathname]);
}
